import { IModuleConfiguration, ModuleConfiguration } from 'src/types/system_types';
import { tokenBearerSessionStrategy } from 'src/support/session/token_bearer_session_strategy';
import { noneSessionStrategy } from 'src/support/session/none_session_strategy';
import { tokenDigestSessionStrategy } from 'src/support/session/token_digest_session_strategy';
import { tokenCookieSessionStrategy } from 'src/support/session/token_cookie_session_strategy';
import { ISessionStrategy } from 'src/types/module_types_authentication';
import { version } from 'src/core/settings';

import ess from'../../../pip/library/pi-ess/pi-ess.js';
import tt from'../../../pip/library/pi-tt/pi-tt.js';

type t_authentication_scheme_type_deprecated = 'token' | 'bearer' | 'server' | 'cookie_header';

type t_authentication_scheme_type = 'token_digest' | 'token_bearer' | 'token_cookie' | 'none';

type t_authentication_scheme_type_all = t_authentication_scheme_type_deprecated | t_authentication_scheme_type;

export interface ISessionModuleConfiguration extends IModuleConfiguration {
  authentication_scheme: t_authentication_scheme_type;

  /**
   * very dangerous method of forcing authentication
   * @note we should disable this on all but local and dev environments
   */
  embedded_session_token: string;

  /**
   * the path in the account record for where to find the token
   * used with `token_bearer` and `token_digest`
   */
  account_token_path: string;
  /**
   * the key on the cookie string where to find CSRF tokens
   * used with `token_cookie`
   */
  cookie_token_key: string;
  /**
   * the key on the header where to place a csrf cookie
   * used with `token_crsf`
   * hard-coded as `Authorization` for `token_bearer` and `token_digest`
   */
  header_token_key: string;

  /**
   * @deprecated, use account_token_path
   */
  token_path: string;
  /**
   * @deprecated, use cookie_token_key
   */
  cookie_key: string;
  /**
   * @deprecated, use header_token_key
   */
  header_key: string;
}

export class SessionModule extends ModuleConfiguration {
  configuration: ISessionModuleConfiguration;
  private readonly __sessionRecord: {
    account: unknown;
  } = {
    account: null
  };

  authenticateStrategy: ISessionStrategy;
  authenticationStrategies: Record<t_authentication_scheme_type, ISessionStrategy> = {
    token_bearer: tokenBearerSessionStrategy,
    token_digest: tokenDigestSessionStrategy,
    token_cookie: tokenCookieSessionStrategy,
    none: noneSessionStrategy
  };

  /**
   * controls if on de-authentication we erase the user record
   */
  authenticationServerControlled: Record<t_authentication_scheme_type, boolean> = {
    token_digest: false,
    token_bearer: false,
    token_cookie: true,
    none: true
  };

  get isAuthenticated() {
    return this.__sessionRecord.account != null;
  }

  get sessionKey() {
    return `ahp.${version}.session.${this.ahp.product.configuration.application.id}`;
  }

  /**
   *
   * @param configuration
   */
  initialize(configuration: ISessionModuleConfiguration) {
    return new Promise((resolve) => {
      this.configuration = this.__defaultConfiguration(configuration);
      this.authenticateStrategy = this.authenticationStrategies[this.configuration.authentication_scheme];

      this.__sessionAccountClear(this);
      this.__sessionAccountSave(this);
      this.__sessionAccountLoad(this);
      this.__sessionAccountStartup(this);
      this.__sessionAccountAutoStartup(this);

      this.__activateAuthenticationStrategy();
      resolve(this.configuration);
    });
  }

  private __activateAuthenticationStrategy() {
    if (this.authenticateStrategy) {
      this.authenticateStrategy.deactivate(this.ahp);
      this.authenticateStrategy.activate(this.ahp);
    }
  }

  activate() {
    return new Promise((resolve) => {
      this.loadSession();
      resolve();
    });
  }

  loadSession() {
    ess.trap({
      ns: 'ahp',
      site: 'session',
      source: 'account',
      action: this.embeddedSession ? 'loadEmbedded' : 'load'
    });
  }

  saveSession() {
    ess.trap({
      ns: 'ahp',
      site: 'session',
      source: 'account',
      action: 'save'
    });
  }

  get account() {
    return this.__sessionRecord.account || {};
  }

  get embeddedSession() {
    return (this.configuration.embedded_session_token.length || '') > 0;
  }

  private __defaultConfiguration(configuration: ISessionModuleConfiguration) {
    const _configuration = tt.mergeWith(
      'clobber',
      {
        authentication_scheme: 'token_digest',
        account_token_path: 'auth_token',
        embedded_session_token: '',
        cookie_token_key: '',
        header_token_key: ''
      },
      configuration
    ) as ISessionModuleConfiguration;

    if (_configuration.token_path != null) {
      _configuration.account_token_path = _configuration.token_path;
    }
    if (_configuration.cookie_key != null) {
      _configuration.cookie_token_key = _configuration.cookie_key;
    }
    if (_configuration.header_key != null) {
      _configuration.header_token_key = _configuration.header_key;
    }

    _configuration.authentication_scheme = this.__mapAuthenticationSchemes(_configuration.authentication_scheme);
    return _configuration;
  }

  private __mapAuthenticationSchemes(authScheme: t_authentication_scheme_type_all) {
    const authSchemeMapping: Record<t_authentication_scheme_type_all, t_authentication_scheme_type> = {
      token_digest: 'token_digest',
      token_bearer: 'token_bearer',
      token_cookie: 'token_cookie',
      none: 'none',
      // deprecated
      token: 'token_digest',
      bearer: 'token_bearer',
      cookie_header: 'token_cookie',
      server: 'none'
    };

    return authSchemeMapping[authScheme];
  }

  private __sessionAccountLoad(session: SessionModule) {
    ess.on(
      {
        ns: 'ahp',
        site: 'session',
        source: 'account',
        action: 'load'
      },
      (_, cb) => {
        try {
          const encodedRecord = localStorage.getItem(this.sessionKey) || '';
          const isServerControlled = this.authenticationServerControlled[this.configuration.authentication_scheme];
          if (!encodedRecord && !isServerControlled) {
            this.__sessionRecord.account = null;
            return;
          }

          this.__sessionRecord.account = Object.freeze(JSON.parse(atob(encodedRecord) || '{}'));

          if (typeof cb === 'function') {
            cb(this.__sessionRecord.account);
          }
        } catch {}
      }
    );
  }

  private __sessionAccountSave(session: SessionModule) {
    ess.on(
      {
        ns: 'ahp',
        site: 'session',
        source: 'account',
        action: ['initiate', 'save']
      },
      (event, cb) => {
        const { data: accountRecord } = event;

        this.__sessionRecord.account = accountRecord;

        try {
          if (accountRecord == null) {
            localStorage.removeItem(this.sessionKey);
          } else {
            localStorage.setItem(this.sessionKey, btoa(JSON.stringify(accountRecord)));
          }
        } catch {}
      }
    );
  }

  private __sessionAccountClear(session: SessionModule) {
    ess.on(
      {
        ns: 'ahp',
        site: 'session',
        source: 'account',
        action: 'clear'
      },
      (_, cb) => {
        try {
          this.__sessionRecord.account = null;
          localStorage.removeItem(this.sessionKey);
        } catch (e) {}
      }
    );
  }

  private __sessionAccountStartup(session: SessionModule) {
    ess.on(
      {
        ns: 'ahp',
        site: 'session',
        source: 'account',
        action: ['initiate', 'clear', 'load']
      },
      () => {
        ess.trap({
          ns: 'ahp',
          site: 'session',
          source: 'account',
          action: 'changeState'
        });
      }
    );
  }

  private __sessionAccountAutoStartup(session: SessionModule) {
    ess.on(
      {
        ns: 'ahp',
        site: 'session',
        source: 'account',
        action: 'loadEmbedded'
      },
      () => {
        const conf = this.configuration;

        this.__sessionRecord.account = {
          ...((this.__sessionRecord.account as object) || {}),
          [conf.account_token_path]: conf.embedded_session_token
        };

        ess.trap({
          ns: 'ahp',
          site: 'session',
          source: 'account',
          action: 'changeState'
        });
      }
    );
  }
}
